/* eslint-disable jsx-a11y/alt-text */
import { Box, Link } from '@mui/material';
import React, { ReactElement, useState } from 'react';
import _ from 'lodash';
import { socialPlatformsIcons } from '../../../investigate/Content/routes/collections/Collection/constants';
import { COLORS } from '../../constants/constants';
import { Tooltip } from '@cellebrite/saas-ui-common';
import { tss } from 'tss-react/mui';

interface IconInfo {
    tooltipContent?: ReactElement;
    altSourceNames?: Array<string>;
    isIconCrossed?: boolean;
    iconName: string;
}

interface CustomIconsListProps {
    list: Array<IconInfo>;
    disableExpansion?: boolean;
    displayUnexpanded?: number;
    listShouldWrap?: boolean;
    displayHover?: boolean;
    columnGap?: string;
}

const unknown = 'unknown';

const CustomIconsList: React.FC<CustomIconsListProps> = ({
    list,
    disableExpansion,
    displayUnexpanded = list.length,
    listShouldWrap = true,
    displayHover,
    columnGap,
}) => {
    const [expanded, setExpanded] = useState(false);
    const { classes: c } = useStyles();
    const expendCountLink = (
        <Link
            key={'expand'}
            sx={{
                'color': displayHover ? COLORS.BRAND : null,
                '&:hover': {
                    color: displayHover ? COLORS.BRAND_HOVER : null,
                    textDecoration: displayHover ? 'underline' : null,
                },
            }}
            onClick={() => {
                if (!disableExpansion) {
                    setExpanded(true);
                }
            }}
        >{`+${list.length - displayUnexpanded}`}</Link>
    );
    const getList = (list, expendedList?) => {
        const result = [];
        list.forEach((item, index) => {
            if (expanded || index < displayUnexpanded || expendedList) {
                let res;
                let platform = socialPlatformsIcons.get(item.iconName?.toUpperCase());
                if (!platform) {
                    platform = item.altSourceNames?.map((altSourceName) =>
                        socialPlatformsIcons.get(altSourceName?.toUpperCase()),
                    )[0];
                }
                res = platform
                    ? require(`../../../common/images/social-icons/${platform}.svg`)
                    : require(`../../../common/images/social-icons/${unknown}.svg`);

                result.push(
                    item.tooltipContent ? (
                        <Tooltip key={`tooltip${index}`} content={item.tooltipContent} placement={'left'}>
                            <Box
                                sx={{
                                    'display': 'flex',
                                    'py': expendedList ? 1 : 0,
                                    'px': expendedList ? 1.5 : 0,
                                    'cursor': expendedList ? 'pointer' : null,
                                    '&:hover': {
                                        backgroundColor: `${COLORS.MUTED_HOVER} !important`,
                                    },
                                }}
                            >
                                <Box className={item.isIconCrossed ? c.crossedContainer : null}>
                                    <img src={res} className={c.icon} />
                                </Box>
                            </Box>
                        </Tooltip>
                    ) : (
                        <Box
                            key={index}
                            sx={{
                                'display': 'flex',
                                'py': expendedList ? 1 : 0,
                                'px': displayHover && expendedList ? 0.7 : expendedList ? 1.5 : 0,
                                'cursor': expendedList && !displayHover ? 'pointer' : null,
                                '&:hover': {
                                    backgroundColor: displayHover ? null : `${COLORS.MUTED_HOVER} !important`,
                                },
                            }}
                        >
                            <Box className={item.isIconCrossed ? c.crossedContainer : null}>
                                <img
                                    src={res}
                                    className={c.icon}
                                    key={`${item.iconName}${index}`}
                                    title={item.iconName}
                                />
                            </Box>
                        </Box>
                    ),
                );
            }
        });
        if (!expanded && list.length > displayUnexpanded && !expendedList) {
            result.push(
                _.find(list.slice(displayUnexpanded), (item) => {
                    return item.tooltipContent;
                }) ? (
                    <Tooltip
                        placement="bottom-end"
                        key={'expand1'}
                        content={
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    rowGap: '0px',
                                    columnGap: '0px',
                                    alignItems: 'center',
                                    maxWidth: 400,
                                }}
                            >
                                {getList(
                                    list.filter((item, index) => index >= displayUnexpanded),
                                    true,
                                )}
                            </Box>
                        }
                    >
                        <Link
                            onClick={() => {
                                if (!disableExpansion) {
                                    setExpanded(true);
                                }
                            }}
                            sx={{ cursor: 'pointer' }}
                        >{`+${list.length - displayUnexpanded}`}</Link>
                    </Tooltip>
                ) : displayHover ? (
                    <Tooltip
                        placement="bottom-end"
                        key={'expand2'}
                        styles={{
                            p: 0,
                            boxShadow:
                                '0px 5px 1px 0px rgba(145, 158, 171, 0.08), 0px 2px 2px 0px rgba(145, 158, 171, 0.16), 0px 1px 4px -2px rgba(145, 158, 171, 0.24)',
                        }}
                        content={
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    rowGap: '0px',
                                    columnGap: '0px',
                                    alignItems: 'center',
                                    maxWidth: 400,
                                }}
                            >
                                {getList(
                                    list.filter((item, index) => index >= displayUnexpanded),
                                    true,
                                )}
                            </Box>
                        }
                    >
                        {expendCountLink}
                    </Tooltip>
                ) : (
                    expendCountLink
                ),
            );
        }
        return result;
    };
    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: listShouldWrap ? 'wrap' : 'no-wrap',
                rowGap: '8px',
                columnGap: columnGap || '13px',
                alignItems: 'center',
            }}
        >
            {getList(list)}
        </Box>
    );
};

const useStyles = tss.create(({ theme }) => ({
    crossedContainer: {
        'position': 'relative',
        '&:before': {
            content: '""',
            position: 'absolute',
            height: '2px',
            width: '34px',
            borderRadius: '2px',
            transform: 'rotate(45deg)',
            top: '9px',
            left: '-8px',
            opacity: 0.4,
            background: theme.palette.clbtBrand.dark,
        },
    },
    icon: {
        width: '16px',
        height: '16px',
        cursor: 'pointer',
    },
}));

export default CustomIconsList;
